/**
 * Tabs custom element.
 *
 * Manages tab switching, ARIA attributes, keyboard navigation,
 * and a sliding indicator. Supports two rendering paths:
 *
 * - Snippet path: tabs and panels are server-rendered in the HTML
 * - Block path: tab buttons start inside panels as [data-tab-button],
 *   and get moved into the [role="tablist"] on init
 *
 * Without JS, panels are stacked with the default panel visible
 * (others have the hidden attribute set server-side).
 */
class ThemeTabs extends HTMLElement {
  #active = 0;
  #cursor = 0;
  #indicator = null;
  #resizeObserver = null;
  #isBlockPath = false;

  connectedCallback() {
    this.#active = parseInt(this.dataset.active || '0', 10);
    this.#cursor = this.#active;
    this.activateOnFocus = this.dataset.activateOnFocus !== 'false';

    const tablist = this.querySelector('[role="tablist"]');
    const buttons = this.querySelectorAll('[data-tab-button]');

    this.#isBlockPath = buttons.length > 0 && tablist != null;

    if (this.#isBlockPath) {
      for (const btn of buttons) {
        tablist.appendChild(btn);
      }
    }

    // Bind events on tabs
    const tabs = this.#tabs;
    for (let i = 0; i < tabs.length; i++) {
      tabs[i].addEventListener('click', () => this.#activate(i));
      tabs[i].addEventListener('focus', () => {
        if (this.activateOnFocus) this.#activate(i);
      });
      tabs[i].addEventListener('keydown', (e) => this.#handleKeydown(e));
    }

    if (tablist) this.#createIndicator(tablist);

    // Skip disabled initial tab
    const activeTab = tabs[this.#active];
    if (activeTab && activeTab.getAttribute('aria-disabled') === 'true') {
      for (let j = 0; j < tabs.length; j++) {
        if (tabs[j].getAttribute('aria-disabled') !== 'true') {
          this.#active = j;
          break;
        }
      }
    }

    this.#cursor = this.#active;
    this.setAttribute('data-activation-direction', 'none');

    this.#sync();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  get #tabs() {
    return [...this.querySelectorAll('[role="tab"]')];
  }

  get #panels() {
    return [...this.querySelectorAll('[role="tabpanel"]')];
  }

  #activate(index) {
    const tab = this.#tabs[index];
    if (tab && tab.getAttribute('aria-disabled') === 'true') return;

    const prev = this.#active;
    this.#active = index;
    this.#cursor = index;

    if (index > prev) this.setAttribute('data-activation-direction', 'right');
    else if (index < prev) this.setAttribute('data-activation-direction', 'left');
    else this.setAttribute('data-activation-direction', 'none');

    this.#sync();
  }

  #handleKeydown(e) {
    const total = this.#tabs.length;
    let handled = true;

    switch (e.key) {
      case 'ArrowRight': this.#cursor = (this.#cursor + 1) % total; break;
      case 'ArrowLeft': this.#cursor = (this.#cursor - 1 + total) % total; break;
      case 'Home': this.#cursor = 0; break;
      case 'End': this.#cursor = total - 1; break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.#activate(this.#cursor);
        return;
      default: handled = false;
    }

    if (handled) {
      e.preventDefault();
      this.#tabs[this.#cursor]?.focus();
      if (this.activateOnFocus) this.#activate(this.#cursor);
    }
  }

  #sync() {
    const tabs = this.#tabs;
    const panels = this.#panels;
    const dir = this.getAttribute('data-activation-direction') || 'none';

    for (let i = 0; i < tabs.length; i++) {
      const isActive = i === this.#active;
      tabs[i].setAttribute('aria-selected', String(isActive));
      tabs[i].setAttribute('tabindex', isActive ? '0' : '-1');
      tabs[i].setAttribute('data-activation-direction', dir);
      tabs[i].toggleAttribute('data-active', isActive);
    }

    for (let i = 0; i < panels.length; i++) {
      const isActive = i === this.#active;
      panels[i].setAttribute('data-index', String(i));
      panels[i].setAttribute('tabindex', isActive ? '0' : '-1');
      panels[i].setAttribute('data-activation-direction', dir);
      panels[i].toggleAttribute('data-active', isActive);
      panels[i].hidden = !isActive;
      panels[i].inert = !isActive;
    }

    this.#syncIndicator();
  }

  #createIndicator(tablist) {
    const el = document.createElement('span');
    el.setAttribute('data-slot', 'tabs-indicator');
    el.setAttribute('role', 'presentation');
    el.className = 'tabs__indicator';
    el.hidden = true;
    tablist.appendChild(el);
    this.#indicator = el;

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => this.#syncIndicator());
      this.#resizeObserver.observe(tablist);
    }
  }

  #syncIndicator() {
    const indicator = this.#indicator;
    if (!indicator) return;

    const tabs = this.#tabs;
    const tablist = this.querySelector('[role="tablist"]');
    const activeTab = tabs[this.#active];

    if (!activeTab || !tablist) {
      indicator.hidden = true;
      return;
    }

    const tabRect = activeTab.getBoundingClientRect();
    const listRect = tablist.getBoundingClientRect();
    const left = tabRect.left - listRect.left + tablist.scrollLeft;
    const top = tabRect.top - listRect.top + tablist.scrollTop;
    const width = activeTab.offsetWidth;
    const height = activeTab.offsetHeight;

    indicator.style.setProperty('--active-tab-left', left + 'px');
    indicator.style.setProperty('--active-tab-top', top + 'px');
    indicator.style.setProperty('--active-tab-right', (tablist.scrollWidth - left - width) + 'px');
    indicator.style.setProperty('--active-tab-bottom', (tablist.scrollHeight - top - height) + 'px');
    indicator.style.setProperty('--active-tab-width', width + 'px');
    indicator.style.setProperty('--active-tab-height', height + 'px');

    indicator.hidden = false;
    if (!indicator.hasAttribute('data-ready')) indicator.setAttribute('data-ready', '');
  }
}

if (!customElements.get('theme-tabs')) {
  customElements.define('theme-tabs', ThemeTabs);
}
